/**
 * Instance resolvers.
 *
 * `initGame` issues tile/token instance IDs of the form `tile-inst-<N>` and
 * `token-inst-<N>` where N is the position in the shuffled draw pile / bag.
 * The mapping from N to the real `tileDefId` / `Wildlife` species is
 * deterministic (cycles through `TILE_DEFS` and `WILDLIFE` respectively),
 * so any consumer can recover the underlying data from the instance ID
 * alone — no separate registry needs to be carried in `GameState`.
 *
 * Used by:
 *   - `init-game.ts` and the market-mutating appliers to denormalise the
 *     real data into `MarketSlot.tileDefId` / `MarketSlot.wildlifeSpecies`
 *     (so the UI can render the actual tile / token without a side lookup).
 *   - `place-tile.applier.ts` (and any future applier) to read the real
 *     tile-def behind a drafted instance.
 *   - `end-turn.applier.ts` to detect the 4-same auto-wipe condition by
 *     species, which previously was stubbed out (P2C → P2E).
 *
 * Starter cluster cells use a separate `starter-<seat>-<i>` instance-ID
 * namespace and carry their `tileDefId` directly on `BoardCell` — these
 * resolvers are intentionally market/draw-pile only.
 */

import { WILDLIFE, type Wildlife } from "../types/wildlife";
import { TILE_DEFS } from "./tile-defs";

const TILE_INSTANCE_PATTERN = /^tile-inst-(\d+)$/;
const TOKEN_INSTANCE_PATTERN = /^token-inst-(\d+)$/;

/** Resolve a draw-pile tile instance ID to its `tileDefId`. */
export const resolveTileDefId = (tileInstanceId: string): string => {
  const match = TILE_INSTANCE_PATTERN.exec(tileInstanceId);
  if (match === null || match[1] === undefined) {
    return TILE_DEFS[0]!.tileDefId;
  }
  const index = parseInt(match[1], 10);
  return (
    TILE_DEFS[index % TILE_DEFS.length]?.tileDefId ?? TILE_DEFS[0]!.tileDefId
  );
};

/** Resolve a wildlife-bag token instance ID to its `Wildlife` species. */
export const resolveWildlifeSpecies = (tokenInstanceId: string): Wildlife => {
  const match = TOKEN_INSTANCE_PATTERN.exec(tokenInstanceId);
  if (match === null || match[1] === undefined) {
    return WILDLIFE[0]!;
  }
  const index = parseInt(match[1], 10);
  return WILDLIFE[index % WILDLIFE.length]!;
};
